import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CountableTimeZonePipe } from 'countable@helpers';
import { AddScheduleModel, BroadcastMessageModel, COLUMN_TYPES, ColumnScheduleModel, DIALOG_MESSAGES, DIALOG_TYPES, DialogScheduleModel, EngagementScheduleModel, GenericResModel, GroupScheduleModel, ROW_TYPES, RowScheduleModel, TrialBalanceScheduleModel, UpdateEngagementScheduleModel, UpdateFormatScheduleModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SchedulesDialogComponent } from '../components/remote-shared/schedules/schedules-dialog/schedules-dialog.component';
import { ScheduleUtil } from '../helpers/schedule/schedule-util';
import { WorkbookService } from './workbook.service';

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {
  scheduleUtil: typeof ScheduleUtil = ScheduleUtil;
  private static readonly API = environment.apiV1 + '/eng/schedule';

  private engagementSchedule : WritableSignal<EngagementScheduleModel> = signal<EngagementScheduleModel>(new EngagementScheduleModel());
  engagementScheduleSignal : Signal<EngagementScheduleModel> = this.engagementSchedule.asReadonly();
  spinner: WritableSignal<boolean> = signal<boolean>(false);
  private refresh = new BehaviorSubject<any>(null);
  refresh$ = this.refresh.asObservable();
  private reorderSchedule = new BehaviorSubject<any>(null);
  reorderSchedule$ = this.reorderSchedule.asObservable();
  insideEngSchedule = new BehaviorSubject<any>(null);
  private resetSchedule = new Subject<any>();
  resetSchedule$ = this.resetSchedule.asObservable();
  private updateChanges = new Subject<string>();
  updateChanges$ = this.updateChanges.asObservable();
  private syncSchedule = new BehaviorSubject<any>(null);
  syncSchedule$ = this.syncSchedule.asObservable();
  private syncCallSpinner = new BehaviorSubject<boolean>(false);
  syncCallSpinner$ = this.syncCallSpinner.asObservable();
  public saveChanges = new BehaviorSubject<boolean>(false);
  totalSchedules: WritableSignal<number> = signal<number>(0);
  manualSync = new BehaviorSubject<boolean>(false);


  private isBroadcastChannelSupported: boolean = typeof BroadcastChannel !== 'undefined';
  private static readonly CHANNEL_NAME = 'schedule';
  private scheduleChannel: BroadcastChannel | null = null;
  private tabId = this.scheduleUtil.getOrCreateTabId(`${ScheduleService.CHANNEL_NAME}-broadcast-tab-id`); // Unique ID per tab
  private messageSubject = new Subject<BroadcastMessageModel>();
  message$ = this.messageSubject.asObservable();

  constructor(private http: HttpClient, public dialog: MatDialog, private countableTimeZonePipe: CountableTimeZonePipe, private toasterService: ToastrService) {  }

  initChannel(): void {
    if(!this.scheduleChannel && this.isBroadcastChannelSupported){
      this.scheduleChannel = new BroadcastChannel(ScheduleService.CHANNEL_NAME);

      this.scheduleChannel.onmessage = (event) => {
        const data = event.data;
        (data.sender == this.tabId) && this.messageSubject.next(data);
      };
    }
  }

  public updateEngagementSchedule(engagementSchedule: Partial<EngagementScheduleModel>): void {
    this.engagementSchedule.update((current) => ({
      ...current,
      ...engagementSchedule
    }));
  }

  public getEngagementSchedule() {
    return this.engagementSchedule();
  }

  setRefresh(value?: any): void {
    const updatedValue = value? value : this.getRefresh();
    this.refresh.next(updatedValue);
  }

  getRefresh(): any {
    return this.refresh.getValue();
  }

  setSync(value?: any): void {
    this.syncSchedule.next(value);
  }

  getSync(): any {
    return this.syncSchedule.getValue();
  }

  setReorder(value?: any): void {
    const updatedValue = value? value : this.getReorder();
    this.reorderSchedule.next(updatedValue);
  }

  getReorder(): any {
    return this.reorderSchedule.getValue();
  }

  sendMessage(message: Partial<BroadcastMessageModel>): void {
    if(this.isBroadcastChannelSupported){
      this.initChannel();
      this.scheduleChannel?.postMessage({
        sender: this.tabId,
        ...message
      });
    } else{
      localStorage.setItem('schedule_preview', JSON.stringify(message));
      setTimeout(() => localStorage.removeItem('schedule_preview'), 0);
    }

  }

  closeChannel(): void {
    if (this.scheduleChannel) {
      this.scheduleChannel.onmessage = null;
      this.scheduleChannel.close();
      this.scheduleChannel = null;
    }
  }

  public updateSpinner(value: boolean): void {
    this.spinner.update(() => value);
  }

  public updatePreview(uuid: string) {
    this.updateChanges.next(uuid);
  }

  public updateSyncCallSpinner(value: boolean): void {
    this.syncCallSpinner.next(value);
  }

  public updateScheduleCount(action: 'add' | 'delete' | 'set', value?: number): void {
    this.totalSchedules.update(count => {
      switch (action) {
        case 'add': return count + 1;
        case 'delete': return count - 1;
        case 'set': return value ?? count;
        default: return count;
      }
    });
  }


  // fetch data from API

  public addSchedules(payload: AddScheduleModel): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(ScheduleService.API + '/detail', payload);
  }

  public deleteSchedule(tbSchId: string): Observable<GenericResModel<string>> {
    return this.http.delete<GenericResModel<string>>(ScheduleService.API + '/detail', {body: tbSchId});
  }

  public getAllSchedules(payload: any): Observable<GenericResModel<any>> {
    return this.http.get<GenericResModel<any>>(ScheduleService.API + '/all', {params: payload});
  }

  public getSchedule(payload: any): Observable<GenericResModel<UpdateEngagementScheduleModel>> {
    return this.http.get<GenericResModel<UpdateEngagementScheduleModel>>(ScheduleService.API + '/detail', {params: payload});
  }

  public commonPutMethod(path: string, payload: any): Observable<GenericResModel<string>> {
    return this.http.put<GenericResModel<string>>(ScheduleService.API + path, payload);
  }

  public updateFormat(path: string, payload: UpdateFormatScheduleModel[]): Observable<GenericResModel<string>> {
    return this.http.put<GenericResModel<string>>(ScheduleService.API + path, payload);
  }

  public updateRows(path: string, payload: RowScheduleModel[]): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(ScheduleService.API + path, payload);
  }

  public deleteColumnsOrRows(path: string, payload: any): Observable<GenericResModel<string>> {
    return this.http.delete<GenericResModel<string>>(ScheduleService.API + path, {body: payload});
  }

  public getTrialBalanceData(payload: any): Observable<GenericResModel<TrialBalanceScheduleModel[]>> {
    return this.http.get<GenericResModel<TrialBalanceScheduleModel[]>>(ScheduleService.API + '/tb', {params: payload});
  }

  public updateScheduleName(path: string, payload: any) {
    this.http.put<GenericResModel<string>>(ScheduleService.API + path, payload).subscribe({
      next: response => {
        if (response.status === 200) {
          this.setRefresh();
        }
      },
      error: err => {
        this.toasterService.error(err.error);
      }
    });
  }




  //   delete schedules
  public confirmDeleteSchedule(selectedSchedule: any, type: DIALOG_TYPES, resetSchedule?: any) {
    // Check if the dialog is already open
    const isDialogOpen = this.dialog.openDialogs.some(
      d => d.componentInstance instanceof SchedulesDialogComponent
    );
    if (!isDialogOpen) {
      const scheduleName = (selectedSchedule?.schedule_name ? selectedSchedule?.schedule_name: ('Schedule '+ selectedSchedule?.sortOrder));
      const headerText = scheduleName +' - '+ (selectedSchedule?.nameChange || selectedSchedule?.groupName || selectedSchedule?.name);
      const schedule = new DialogScheduleModel();
      schedule.headerText = type === DIALOG_TYPES.RESET ? `Reset ${headerText}` : `Delete ${headerText}`;
      schedule.subHeaderText = type === DIALOG_TYPES.RESET ? DIALOG_MESSAGES.SUB_HEADER_RESET : DIALOG_MESSAGES.SUB_HEADER_TEXT_SCHEDULE;
      schedule.buttonCancelText = DIALOG_MESSAGES.BUTTON_CANCEL_TEXT;
      schedule.buttonConfirmText = type === DIALOG_TYPES.RESET ? DIALOG_MESSAGES.BUTTON_RESET_CONFIRM_TEXT : DIALOG_MESSAGES.BUTTON_CONFIRM_TEXT;
      schedule.errorText = type === DIALOG_TYPES.RESET ? DIALOG_MESSAGES.ERROR_TEXT_RESET : null;
      schedule.subInfoText = type === DIALOG_TYPES.RESET ? DIALOG_MESSAGES.SUB_INFO_TEXT_RESET : null;
      schedule.type = type;
      schedule.selectedSchedule = selectedSchedule;
      const matDialogConfig = new MatDialogConfig();
      matDialogConfig.data = schedule;
      matDialogConfig.disableClose = true;
      matDialogConfig.autoFocus = true;
      this.dialog.open(SchedulesDialogComponent, matDialogConfig).afterClosed().subscribe(result => {
        if (result?.success && type === DIALOG_TYPES.RESET) {
          const scheduleForm = {
            groupName: selectedSchedule?.name,
            orientation: selectedSchedule?.isCollapse
          }
          const scheduleList = this.scheduleUtil.createSchedule({type: resetSchedule?.subGroups?.length > 1 ? DIALOG_TYPES.GROUP : DIALOG_TYPES.SCHEDULE},scheduleForm, resetSchedule?.subGroups);
          this.addOrResetSchedules(scheduleList, resetSchedule?.prev, selectedSchedule?.sortOrder, selectedSchedule?.submodule);
        }else if(result?.success){
          this.updateScheduleCount('delete');
          this.setReorder(selectedSchedule?.uuid || selectedSchedule?.combined_id);
        }
      });
    }
  }

  //  Add and reset schedules
  public addOrResetSchedules(scheduleData: any, isPreviousYear: boolean, sortOrder?: number, isBSSch?: any) {
    let columns: ColumnScheduleModel[] = [];
    columns.push({
      type: COLUMN_TYPES.CY,
      name: this.countableTimeZonePipe.transform(this.engagementSchedule().engagementDetails.yearEnd, 'MMMM d, yyyy', 'UTC'),
      isBold: true
    });
    if (isPreviousYear) {
      columns.push({
        type: COLUMN_TYPES.PY,
        name: this.countableTimeZonePipe.transform(this.engagementSchedule().engagementDetails.yearEnd, 'MMMM d, yyyy', 'UTC', true)
      });
    }

    let currentSubmodule = isBSSch ? 'balancesheet' : 'incomeloss';
    const submoduleId = sortOrder ? isBSSch : this.engagementSchedule()?.leftSideMenu?.find((menu: any) => menu.uirouting === currentSubmodule)?.submoduleid;

    const payload = new AddScheduleModel();
    payload.groupName = scheduleData.groupName;
    payload.isManual = scheduleData?.isManual ? scheduleData.isManual : false;
    payload.isCollapse = scheduleData?.isCollapse ? scheduleData.isCollapse : false;
    payload.submoduleId = scheduleData?.isManual ? null : submoduleId;
    let currentYearTotal = 0;
    let previousYearTotal = 0;
    if (scheduleData?.child?.length) {
      scheduleData?.child.forEach((group: any) => {
        currentYearTotal = 0;
        previousYearTotal =0;
        const groupLevel = new GroupScheduleModel();
        groupLevel.name = group.name;
        const parts = group?.chldid ? group.chldid.split('.') : [];
        groupLevel.groupLinkId = group?.chldid ? +parts[parts.length-1] : null;
        if (group?.trialbal?.length) {
          group?.trialbal?.forEach((row: any, i: number) => {
            const addRow = new RowScheduleModel();
            addRow.tbId = row.trailbalanceid;
            addRow.accountName = row.accountname;
            addRow.cyAmount = row?.Currbal ? this.scheduleUtil.convertToNumber(row.Currbal) : 0;
            addRow.pyAmount = isPreviousYear && row?.Prevbal ? this.scheduleUtil.convertToNumber(row.Prevbal) : 0;
            addRow.sortOrder = i + 1;
            addRow.type = ROW_TYPES.GENERAL;
            currentYearTotal += addRow.cyAmount;
            previousYearTotal += addRow.pyAmount;
            groupLevel.rows.push(addRow);
            //   for subtotal row
            if (payload.groupName && (i === group.trialbal?.length - 1)) {
              const subTotal = new RowScheduleModel();
              subTotal.tbId = null;
              subTotal.accountName = group?.totalname;
              subTotal.cyAmount = currentYearTotal;
              subTotal.pyAmount = isPreviousYear ? previousYearTotal : 0;
              subTotal.sortOrder = addRow.sortOrder+1;
              subTotal.type = ROW_TYPES.SUBTOTAL;
              subTotal.hasBottomLine = true;
              subTotal.hasTopLine = true;
              groupLevel.rows.push(subTotal);
            }
          });
        }
        payload.schedules.push(groupLevel);
      });
    }


    payload.engId = this.engagementSchedule().engagementId;
    payload.acctYear = +this.countableTimeZonePipe.transform(this.engagementSchedule().engagementDetails.yearEnd,
      'yyyy', 'UTC');
    payload.columns = columns;
    payload.sortOrder = sortOrder ? sortOrder : this.totalSchedules();
    this.updateSpinner(true);
    this.addSchedules(payload).pipe(finalize(() => this.updateSpinner(false))).subscribe({
      next: response => {
        if (response.status === 200 && response.data) {
          const scheduleName = payload.groupName ? payload.groupName : payload.schedules[0].name;
          let message = sortOrder ?
            `Schedule ${payload.sortOrder} - ${scheduleName} reset to default successfully` :
            `Schedule ${payload.sortOrder} - ${scheduleName} added successfully under Schedules section in the LHS menu`;
          message = payload.isManual ? `Schedule ${payload.sortOrder} - ${scheduleName} created successfully` : message;
          this.toasterService.success(message);
          if(sortOrder){
            this.resetSchedule.next(response.data);
          }else{
            // this.workbookService.sendMessage({uuid: response.data , type: 'schedule', id: null});
            this.setRefresh();
          }
        } else {
          response?.messages?.length ? this.handleMessage(response.messages) :
            this.handleMessage(response?.errors);
        }
      }, error: e => { this.toasterService.error(e.error); }
    });
  }

  public handleMessage(list: string[]) {
    if (list?.length) {
      list.forEach(msg => {
        this.toasterService.error(msg);
      });
    }
  }

}
